/**
 * Order Management — order/item normalization, timeline, dates, and countdown logic.
 */

import {
  ORDER_STATUS_SEQUENCE,
  PICKUP_IN_STORE_SEQUENCE,
  TERMINAL_STATUSES,
  TERMINAL_TRACKING_STATUSES,
} from './restaurantDeliveryConfig';

function distanceAndETA(start, end) {
  if (!start || !end) return { roadMiles: 0, etaMinutes: 0 };
  const miles = haversineMiles(start[0], start[1], end[0], end[1]);
  const etaMinutes = miles > 0 ? Math.max(5, Math.round(miles * 4)) : 0;
  return { roadMiles: miles, etaMinutes };
}

/** GeoJSON Point [lng, lat] → Leaflet [lat, lng]. */
export function parseGeoLocationToLatLng(geo) {
  if (!geo) return null;
  const coords = geo.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const a = Number(coords[0]);
  const b = Number(coords[1]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const looksLonLat = Math.abs(a) <= 180 && Math.abs(b) <= 90 && Math.abs(a) >= Math.abs(b);
  if (looksLonLat) return [b, a];
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [a, b];
  return [b, a];
}

function haversineMiles(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function getStatusStyle(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('delivered') || s.includes('picked in store'))
    return { bg: '#D1FAE5', color: '#065F46' };
  if (s.includes('cancel') || s.includes('damaged') || s.includes('unpicked'))
    return { bg: '#FEE2E2', color: '#991B1B' };
  if (s === 'pending') return { bg: '#FEF3C7', color: '#92400E' };
  if (s.includes('accepted')) return { bg: '#DBEAFE', color: '#1E40AF' };
  if (s.includes('ready')) return { bg: '#FEF9C3', color: '#713F12' };
  if (s.includes('pickup pending')) return { bg: '#FFE4CC', color: '#C2410C' };
  if (s.includes('picked the order')) return { bg: '#EDE9FE', color: '#5B21B6' };
  if (s.includes('door')) return { bg: '#E0F2FE', color: '#0C4A6E' };
  return { bg: '#F3F4F6', color: '#374151' };
}

export function formatPromotionLabel(promo) {
  const s = String(promo || '').trim();
  if (!s || s.toLowerCase() === 'none') return null;
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getItemPromotion(raw) {
  const value = raw?.product_promotion ?? raw?.promotion;
  return formatPromotionLabel(value);
}

export function normalizeExtraToppings(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((t) => ({
    name: t.topping_name ?? t.name ?? '—',
    qty: t.topping_qty ?? t.qty ?? 1,
    price: t.price != null ? Number(t.price) : null,
  }));
}

export function getItemLineTotal(item) {
  const qty = Number(item.qty || 1);
  const base = Number(item.price || 0);
  const toppingsPerUnit = (item.toppings || []).reduce(
    (sum, t) => sum + Number(t.price || 0) * Number(t.qty || 1),
    0
  );
  return (base + toppingsPerUnit) * qty;
}

export function normalizeRegularItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.price != null ? Number(r.price) : null,
    notes: null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

export function normalizeMealItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.mealItemPrice != null ? Number(r.mealItemPrice) : null,
    notes: r.product_category
      ? String(r.product_category).replace(/_/g, ' ')
      : null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

export function normalizeUpsellItems(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((r) => ({
    name: r.item ?? r.name ?? '—',
    qty: r.qty ?? 1,
    price: r.price != null ? Number(r.price) : null,
    notes: null,
    toppings: normalizeExtraToppings(r.extra_toppings),
    promotion: getItemPromotion(r),
  }));
}

function isSelectablePickupFreeItem(row) {
  const name = String(row?.name ?? '')
    .trim()
    .toLowerCase();
  return Boolean(name && name !== 'none');
}

function normalizePickupFreeItemRow(free) {
  const name = String(free.name).trim();
  const size = String(free.size ?? '')
    .trim()
    .toLowerCase();
  const sizeLabel = size && size !== 'none' ? ` (${String(free.size).trim()})` : '';
  return {
    name: `${name}${sizeLabel}`,
    qty: free.qty ?? 1,
    price: 0,
    notes: 'Included free',
    toppings: [],
    promotion: null,
    isFree: true,
  };
}

/** Maps order `pickup_free_items[]` ({ name, size }) into display rows for the info panel. */
export function normalizeFreeItem(order) {
  const rawItems = Array.isArray(order?.pickup_free_items)
    ? order.pickup_free_items
    : order?.pickup_free_item
      ? [order.pickup_free_item]
      : [];

  return rawItems.filter(isSelectablePickupFreeItem).map(normalizePickupFreeItemRow);
}

export function normalizeOrderStatus(status) {
  return String(status || '')
    .toLowerCase()
    .trim()
    .replace(/_/g, ' ');
}

export function buildStatusLogMap(order) {
  const logs = Array.isArray(order?.order_logs) ? order.order_logs : [];
  const map = new Map();

  [...logs]
    .filter((l) => l.order_status)
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time))
    .forEach((l) => {
      const key = normalizeOrderStatus(l.order_status);
      map.set(key, {
        status: l.order_status,
        timestamp: l.update_time,
        user_type: l.user_type ?? null,
        user_email: l.user_email ?? null,
        notes: l.notes ?? null,
      });
    });

  if (!map.has('pending') && order?.create_at) {
    map.set('pending', {
      status: 'pending',
      timestamp: order.create_at,
      user_type: 'system',
      user_email: null,
      notes: 'Order placed',
    });
  }

  return map;
}

export function getOrderStatusSequence(order) {
  const logMap = buildStatusLogMap(order);
  const current = normalizeOrderStatus(order?.internal_order_status);

  const useInStore =
    current.includes('picked in store') ||
    [...logMap.keys()].some((k) => k.includes('picked in store'));

  let sequence = useInStore ? [...PICKUP_IN_STORE_SEQUENCE] : [...ORDER_STATUS_SEQUENCE];

  const terminal = TERMINAL_TRACKING_STATUSES.find(
    (s) => logMap.has(normalizeOrderStatus(s)) || current === normalizeOrderStatus(s)
  );
  if (terminal && !sequence.some((s) => normalizeOrderStatus(s) === normalizeOrderStatus(terminal))) {
    sequence = [...sequence, terminal];
  }

  return sequence;
}

export function buildOrderTimeline(order) {
  const sequence = getOrderStatusSequence(order);
  const logMap = buildStatusLogMap(order);
  const currentNorm = normalizeOrderStatus(order?.internal_order_status);

  const entries = sequence.map((status) => {
    const norm = normalizeOrderStatus(status);
    const log = logMap.get(norm);
    const isComplete = !!log;
    const isCurrent = currentNorm === norm;
    return {
      status: log?.status ?? status,
      timestamp: log?.timestamp ?? null,
      user_type: log?.user_type ?? null,
      user_email: log?.user_email ?? null,
      notes: log?.notes ?? null,
      isComplete,
      isCurrent,
      isPending: !isComplete && !isCurrent,
    };
  });

  return entries.reverse();
}

const PENDING_STATUS_NORM = normalizeOrderStatus('pending');

/** Index of the order's current step in the tracking sequence (internal_order_status wins). */
function resolveTimelineCurrentIndex(sequence, order) {
  const currentNorm = normalizeOrderStatus(order?.internal_order_status);
  if (!currentNorm) return 0;

  const direct = sequence.findIndex((s) => normalizeOrderStatus(s) === currentNorm);
  if (direct >= 0) return direct;

  const rawLogs = (Array.isArray(order?.order_logs) ? order.order_logs : [])
    .filter((l) => l?.order_status)
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time));

  if (rawLogs.length) {
    const latestNorm = normalizeOrderStatus(rawLogs[rawLogs.length - 1].order_status);
    const fromLog = sequence.findIndex((s) => normalizeOrderStatus(s) === latestNorm);
    if (fromLog >= 0) return fromLog;
  }

  return 0;
}

/**
 * Map tracking timeline — progress follows status sequence order.
 * Steps before internal_order_status are complete; current step is active; rest are upcoming.
 * Timestamps come from order_logs only for reached steps (avoids stray log entries).
 */
export function buildOrderTimelineFromLogs(order) {
  const sequence = getOrderStatusSequence(order);
  const logMap = buildStatusLogMap(order);
  const currentNorm = normalizeOrderStatus(order?.internal_order_status);
  const currentIndex = resolveTimelineCurrentIndex(sequence, order);

  const entries = sequence.map((status, index) => {
    const norm = normalizeOrderStatus(status);
    const log = logMap.get(norm);

    if (norm === PENDING_STATUS_NORM) {
      const pendingLog = logMap.get(PENDING_STATUS_NORM);
      return {
        status: pendingLog?.status ?? 'pending',
        timestamp: pendingLog?.timestamp ?? order?.create_at ?? null,
        user_type: pendingLog?.user_type ?? null,
        user_email: pendingLog?.user_email ?? null,
        notes: pendingLog?.notes ?? null,
        isComplete: true,
        isCurrent: currentNorm === PENDING_STATUS_NORM,
        isPending: false,
      };
    }

    const isCurrent = index === currentIndex;
    const isComplete = index < currentIndex;
    const isPending = index > currentIndex;
    const reached = isComplete || isCurrent;

    return {
      status: log?.status ?? status,
      timestamp: reached ? log?.timestamp ?? null : null,
      user_type: log?.user_type ?? null,
      user_email: log?.user_email ?? null,
      notes: log?.notes ?? null,
      isComplete,
      isCurrent,
      isPending,
    };
  });

  return entries.reverse();
}

export function fmtDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function fmtTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** End of delivery_time_slot for "Delivery Before" labels (e.g. "11:00 AM"). */
export function formatDeliveryBeforeTime(slotStr) {
  if (!slotStr || String(slotStr).trim() === '') return null;
  const slotParts = String(slotStr).split(' - ');
  const endTimeStr = (slotParts.length >= 2 ? slotParts[1] : slotParts[0]).trim();
  if (!endTimeStr) return null;
  return endTimeStr.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/** Start of delivery_time_slot (e.g. "9:00 AM"). */
export function formatSlotStartTime(slotStr) {
  if (!slotStr || String(slotStr).trim() === '') return null;
  const startTimeStr = String(slotStr).split(' - ')[0]?.trim();
  if (!startTimeStr) return null;
  return startTimeStr.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

export function isOrderPickedUp(order) {
  const status = String(order?.internal_order_status ?? '').toLowerCase();
  if (status.includes('picked the order')) return true;
  if (status.includes('at the door')) return true;
  if (status.includes('delivered')) return true;
  if (status.includes('picked in store')) return true;
  if (order?.driver_information?.order_picked_time) return true;
  return false;
}

/** Matches Order Management delivery-type filter (e.g. store pickup, not home delivery). */
export function isPickupDeliveryOrder(order) {
  const type = String(order?.delivery_type ?? '').toLowerCase();
  return type.includes('pickup');
}

export function isOrderDelivered(order) {
  const status = String(order?.internal_order_status ?? '').toLowerCase();
  return status.includes('delivered') || status.includes('picked in store');
}

/** Latest delivered / picked-in-store timestamp from order_logs, then driver_information. */
export function getOrderDeliveredTimestamp(order) {
  const logs = Array.isArray(order?.order_logs) ? order.order_logs : [];
  const terminalLogs = logs
    .filter((l) => {
      const s = normalizeOrderStatus(l.order_status);
      return s.includes('delivered') || s.includes('picked in store');
    })
    .sort((a, b) => new Date(b.update_time) - new Date(a.update_time));

  if (terminalLogs.length) return terminalLogs[0].update_time;

  const driverDelivered = order?.driver_information?.order_delivered_time;
  if (driverDelivered) return driverDelivered;

  return null;
}

export function getOrderDeliveredFooter(order) {
  if (!isOrderDelivered(order)) return null;

  const status = String(order?.internal_order_status ?? '').toLowerCase();
  const time = fmtTime(getOrderDeliveredTimestamp(order));
  const label = status.includes('picked in store') ? 'Picked in store at' : 'Order delivered at';

  return { label, time: time ?? '--' };
}

/** Estimated pickup time for driver-assigned orders not yet picked up. */
export function formatEstimatedPickupTime(order, driver) {
  const candidates = [
    order?.estimated_pickup_time,
    order?.estimated_pickup,
    order?.pickup_time,
    driver?.estimated_pickup_time,
    driver?.estimated_pickup,
    driver?.pickup_time,
    driver?.order_accepted_time,
  ];

  for (const value of candidates) {
    if (value == null || String(value).trim() === '') continue;
    const formatted = fmtTime(value);
    if (formatted) return formatted;
    const raw = String(value).trim();
    if (/am|pm/i.test(raw)) {
      return raw.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
    }
  }

  return formatSlotStartTime(order?.delivery_time_slot);
}

function pickEtaField(eta, ...keys) {
  if (!eta || typeof eta !== 'object') return null;
  for (const key of keys) {
    const value = eta[key];
    if (value != null && value !== '') return value;
  }
  return null;
}

function formatEtaDistance(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isNaN(n)) {
    const rounded = Number.isInteger(n) ? String(n) : n.toFixed(1);
    return `${rounded}m`;
  }
  const raw = String(value).trim();
  if (/mi|km|m\b/i.test(raw)) return raw.replace(/\s*mi\b/i, 'm');
  return raw;
}

function formatEtaMinutes(value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value);
  if (!Number.isNaN(n)) return `${Math.round(n)} Min`;
  const raw = String(value).trim();
  if (/min/i.test(raw)) return raw.replace(/\bmin\b/i, 'Min');
  return `${raw} Min`;
}

function segmentFromEtaObject(obj) {
  if (obj == null) return null;
  if (typeof obj === 'string') {
    const parts = obj.split('|').map((p) => p.trim());
    if (parts.length >= 2) {
      return {
        distance: parts[0] || null,
        time: parts[1] || null,
      };
    }
    return null;
  }
  if (typeof obj !== 'object') return null;

  const distance = formatEtaDistance(
    pickEtaField(obj, 'distanceMiles', 'miles', 'straightLineMiles', 'distance', 'distance_miles')
  );
  const time = formatEtaMinutes(
    pickEtaField(obj, 'etaMinutes', 'minutes', 'time', 'duration', 'eta_minutes')
  );

  if (!distance && !time) return null;
  return { distance, time };
}

export function formatRouteSegmentLabel(segment) {
  if (!segment?.distance && !segment?.time) return '--';
  return `${segment.distance ?? '--'} | ${segment.time ?? '--'}`;
}

export function orderHasReadyForPickup(order) {
  return buildStatusLogMap(order).has(normalizeOrderStatus('order ready for pickup'));
}

export function orderHasDriverAssigned(order) {
  const driver = order?.driver_information;
  if (driver?.email != null || driver?.first_name != null) return true;
  return (order?.order_logs ?? []).some((l) => {
    const s = normalizeOrderStatus(l.order_status);
    return (
      s.includes('pickup pending') ||
      s.includes('picked the order') ||
      s.includes('at the door')
    );
  });
}

/** Log with driver_coordinates when driver accepts / pickup pending. */
export function findDriverAssignmentLog(order) {
  const logs = (Array.isArray(order?.order_logs) ? order.order_logs : [])
    .filter(
      (l) =>
        Array.isArray(l?.driver_coordinates) &&
        l.driver_coordinates.length >= 2 &&
        Number.isFinite(Number(l.driver_coordinates[0])) &&
        Number.isFinite(Number(l.driver_coordinates[1]))
    )
    .sort((a, b) => new Date(a.update_time) - new Date(b.update_time));

  const pickupPending = logs.filter((l) =>
    normalizeOrderStatus(l.order_status).includes('pickup pending')
  );
  if (pickupPending.length) return pickupPending[pickupPending.length - 1];
  return logs.length ? logs[logs.length - 1] : null;
}

function driverCoordsToLatLng(coords) {
  const fromGeo = parseGeoLocationToLatLng({ coordinates: coords });
  if (fromGeo) return fromGeo;
  const [a, b] = coords.map(Number);
  const looksLonLat = Math.abs(a) > Math.abs(b) && Math.abs(a) <= 180 && Math.abs(b) <= 90;
  return looksLonLat ? [b, a] : [a, b];
}

/**
 * Driver → kitchen distance/time when kitchen is ready and driver is assigned.
 * Uses driver_coordinates from the assignment order log + kitchen_geolocation.
 */
export function buildDriverToKitchenSegmentFromLogs(order) {
  if (!orderHasReadyForPickup(order)) return null;
  if (!orderHasDriverAssigned(order)) return null;
  if (isOrderPickedUp(order)) return null;

  const assignmentLog = findDriverAssignmentLog(order);
  if (!assignmentLog) return null;

  const kitchenLatLng = parseGeoLocationToLatLng(order?.kitchen_geolocation);
  if (!kitchenLatLng) return null;

  let driverLatLng;
  try {
    driverLatLng = driverCoordsToLatLng(assignmentLog.driver_coordinates);
  } catch {
    return null;
  }

  try {
    const eta = distanceAndETA(driverLatLng, kitchenLatLng);
    return segmentFromEtaObject({
      straightLineMiles: eta.roadMiles,
      etaMinutes: eta.etaMinutes,
    });
  } catch {
    return null;
  }
}

/** Driver → kitchen and kitchen → customer legs from order.ETA (or order.eta). */
export function buildRouteEtaSegments(order) {
  const eta = order?.ETA ?? order?.eta ?? null;
  const driverToKitchenFromLogs = buildDriverToKitchenSegmentFromLogs(order);

  if (!eta || typeof eta !== 'object') {
    return {
      driverToKitchen: driverToKitchenFromLogs,
      kitchenToCustomer: null,
      driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchenFromLogs),
      kitchenToCustomerLabel: '--',
    };
  }

  if (Array.isArray(eta.segments) && eta.segments.length >= 2) {
    let driverToKitchen = segmentFromEtaObject(eta.segments[0]);
    const kitchenToCustomer = segmentFromEtaObject(eta.segments[1]);
    if (driverToKitchenFromLogs) driverToKitchen = driverToKitchenFromLogs;
    return {
      driverToKitchen,
      kitchenToCustomer,
      driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchen),
      kitchenToCustomerLabel: formatRouteSegmentLabel(kitchenToCustomer),
    };
  }

  let driverToKitchen = segmentFromEtaObject(
    pickEtaField(eta, 'driverToKitchen', 'driver_to_kitchen', 'driverToStore', 'driver_to_store', 'pickupLeg', 'leg1')
  );
  let kitchenToCustomer = segmentFromEtaObject(
    pickEtaField(
      eta,
      'kitchenToCustomer',
      'kitchen_to_customer',
      'storeToCustomer',
      'store_to_customer',
      'deliveryLeg',
      'leg2'
    )
  );

  if (!kitchenToCustomer) {
    kitchenToCustomer = segmentFromEtaObject({
      straightLineMiles: pickEtaField(eta, 'straightLineMiles', 'straight_line_miles', 'miles'),
      etaMinutes: pickEtaField(eta, 'etaMinutes', 'eta_minutes', 'minutes'),
    });
  }

  if (!driverToKitchen) {
    driverToKitchen = segmentFromEtaObject({
      straightLineMiles: pickEtaField(eta, 'driverStraightLineMiles', 'driverMiles', 'pickupMiles'),
      etaMinutes: pickEtaField(eta, 'driverEtaMinutes', 'pickupMinutes', 'driverMinutes'),
    });
  }

  if (driverToKitchenFromLogs) {
    driverToKitchen = driverToKitchenFromLogs;
  }

  return {
    driverToKitchen,
    kitchenToCustomer,
    driverToKitchenLabel: formatRouteSegmentLabel(driverToKitchen),
    kitchenToCustomerLabel: formatRouteSegmentLabel(kitchenToCustomer),
  };
}

export function fmtDateTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatStatusLabel(s) {
  return String(s || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS = {
  regionaldriver: 'Regional driver',
  kitchendedicateddriver: 'Kitchen dedicated',
};

/** Human-readable label for dedicated driver account_type (avoids broken wraps on compound strings). */
export function formatDedicatedDriverAccountType(accountType) {
  const key = String(accountType ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS[key]) {
    return DEDICATED_DRIVER_ACCOUNT_TYPE_LABELS[key];
  }
  if (!key) return '—';
  return key
    .replace(/(kitchen)(dedicated)/i, '$1 $2')
    .replace(/(dedicated)(driver)/i, '$1 $2')
    .replace(/(regional)(driver)/i, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getDedicatedDriverAccountTypeStyle(accountType) {
  const key = String(accountType ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (key === 'regionaldriver') {
    return { bg: '#EDE9FE', color: '#5B21B6' };
  }
  return { bg: '#DBEAFE', color: '#1E40AF' };
}

export function calcRemainingTime(order, now) {
  const status = String(order?.internal_order_status ?? '');
  if (TERMINAL_STATUSES.has(status)) return null;

  const dateStr = order?.delivery_date;
  const slotStr = order?.delivery_time_slot;
  if (!dateStr || !slotStr) return null;

  const slotParts = slotStr.split(' - ');
  const endTimeStr = (slotParts.length >= 2 ? slotParts[1] : slotParts[0]).trim();

  const dateParts = dateStr.split('/');
  if (dateParts.length !== 3) return null;
  const [month, day, year] = dateParts;

  const timeMatch = endTimeStr.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!timeMatch) return null;

  let hours = parseInt(timeMatch[1], 10);
  const minutes = parseInt(timeMatch[2], 10);
  const ampm = timeMatch[3].toLowerCase();
  if (ampm === 'pm' && hours !== 12) hours += 12;
  if (ampm === 'am' && hours === 12) hours = 0;

  const deadline = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    hours,
    minutes,
    0,
    0
  );

  const diffMs = deadline.getTime() - now;
  const totalSeconds = Math.floor(diffMs / 1000);

  return {
    totalSeconds,
    isOverdue: totalSeconds < 0,
    isCritical: totalSeconds < 30 * 60,
  };
}

export function formatRemainingLabel(totalSeconds) {
  const overdue = totalSeconds < 0;
  const abs = Math.abs(totalSeconds);
  const prefix = overdue ? '−' : '';

  if (abs < 3600) {
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${prefix}${m}:${String(s).padStart(2, '0')}`;
  }

  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  let label = `${prefix}${h}h ${m}m`;
  if (s > 0) label += ` ${s}s`;
  return label;
}

const TIME_LEFT_WARNING_SECONDS = 10 * 60;

/** Card urgency: green (>10m), yellow (≤10m), red (overdue). */
export function getTimeLeftUrgency(totalSeconds) {
  if (totalSeconds < 0) return 'overdue';
  if (totalSeconds <= TIME_LEFT_WARNING_SECONDS) return 'warning';
  return 'ok';
}

function padTimeUnit(value) {
  return String(value).padStart(2, '0');
}

/** Live timer label for order card Time Left: HH:MM:SS or MM:SS (prefixed − when overdue). */
export function formatTimeLeftCardLabel(totalSeconds) {
  const overdue = totalSeconds < 0;
  const abs = Math.abs(Math.floor(totalSeconds));
  const prefix = overdue ? '−' : '';

  if (abs >= 3600) {
    const hours = Math.floor(abs / 3600);
    const minutes = Math.floor((abs % 3600) / 60);
    const seconds = abs % 60;
    return `${prefix}${padTimeUnit(hours)}:${padTimeUnit(minutes)}:${padTimeUnit(seconds)}`;
  }

  const minutes = Math.floor(abs / 60);
  const seconds = abs % 60;
  return `${prefix}${padTimeUnit(minutes)}:${padTimeUnit(seconds)}`;
}

export function htmlDateToMDY(v) {
  if (!v) return '';
  const [y, m, d] = v.split('-');
  return `${m}/${d}/${y}`;
}

export function mdyToHtmlDate(v) {
  if (!v) return '';
  const parts = v.split('/');
  if (parts.length !== 3) return '';
  const [m, d, y] = parts;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Short status label + colors for delivery tracking order cards. */
export function getCardStatusDisplay(internalStatus) {
  const s = String(internalStatus ?? '').toLowerCase();
  if (s.includes('picked the order') || s.includes('at the door')) {
    return { label: 'In-transit', bg: '#E0F2FE', color: '#0284C7' };
  }
  if (s.includes('pickup pending')) {
    return { label: 'Awaiting pickup', bg: '#E0F2FE', color: '#0284C7' };
  }
  if (s === 'pending') {
    return { label: 'Pending', bg: '#FEF3C7', color: '#92400E' };
  }
  if (s.includes('accepted by kitchen')) {
    return { label: 'Preparing', bg: '#DBEAFE', color: '#1E40AF' };
  }
  if (s.includes('ready for pickup')) {
    return { label: 'Ready', bg: '#E0F2FE', color: '#0284C7' };
  }
  if (s.includes('delivered') || s.includes('picked in store')) {
    return { label: 'Delivered', bg: '#D1FAE5', color: '#065F46' };
  }
  if (s.includes('cancel') || s.includes('damaged') || s.includes('unpicked')) {
    return { label: 'Cancelled', bg: '#FEE2E2', color: '#991B1B' };
  }
  if (s.includes('ready')) return { label: 'Ready', bg: '#FEF9C3', color: '#713F12' };
  if (s.includes('accepted')) return { label: 'Preparing', bg: '#DBEAFE', color: '#1E40AF' };
  const { bg, color } = getStatusStyle(internalStatus);
  const label = String(internalStatus || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label, bg, color };
}

/** Minutes until end of delivery_time_slot (0 if past). */
export function getMinutesUntilSlotEnd(order) {
  const slot = order?.delivery_time_slot;
  const dateStr = order?.delivery_date;
  if (!slot || !dateStr) return null;

  const endPart = String(slot).split(' - ')[1]?.trim() ?? String(slot).split(' - ')[0]?.trim();
  if (!endPart) return null;

  const [mm, dd, yyyy] = String(dateStr).split('/');
  if (!mm || !dd || !yyyy) return null;

  const parsed = new Date(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')} ${endPart}`);
  if (Number.isNaN(parsed.getTime())) return null;

  const diffMs = parsed.getTime() - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60));
}

/** Miles + minutes for the card route strip (ETA segments or geolocation fallback). */
export function getOrderCardRouteSummary(rawOrder, routeMode) {
  const eta = rawOrder?.ETA ?? rawOrder?.eta;
  const segments = Array.isArray(eta?.segments) ? eta.segments : [];
  let miles = 0;
  let minutes = 0;

  if (routeMode === 'picked_up' && segments.length >= 2) {
    miles = Number(segments[1]?.straightLineMiles) || 0;
    minutes = Number(segments[1]?.etaMinutes) || 0;
  } else if (segments.length) {
    segments.forEach((seg) => {
      miles += Number(seg?.straightLineMiles) || 0;
      minutes += Number(seg?.etaMinutes) || 0;
    });
  }

  if (miles <= 0) {
    const kitchen = parseGeoLocationToLatLng(rawOrder?.kitchen_geolocation);
    const customer = parseGeoLocationToLatLng(rawOrder?.customer_geolocation);
    if (kitchen && customer) {
      miles = haversineMiles(kitchen[0], kitchen[1], customer[0], customer[1]);
      if (minutes <= 0) minutes = miles > 0 ? Math.max(5, Math.round(miles * 4)) : 0;
    }
  }

  if (minutes <= 0) {
    const slotMinutes = getMinutesUntilSlotEnd(rawOrder);
    if (slotMinutes != null) minutes = slotMinutes;
  }

  const milesLabel =
    miles > 0 ? `${Number.isInteger(miles) ? miles : miles.toFixed(1)} mile` : null;
  const timeLeftMinutes = minutes > 0 ? Math.round(minutes) : null;

  return { milesLabel, timeLeftMinutes };
}

export function formatDisplayOrderNo(orderNo) {
  const s = String(orderNo ?? '').trim();
  if (!s) return '—';
  const payMatch = s.match(/PAY[\w]+/i);
  if (payMatch) return payMatch[0];
  const parts = s.split('-');
  return parts[parts.length - 1] || s;
}

/** Route bar fill: 0% until driver, ~50% when assigned, scales up after pickup, 100% delivered. */
export function getOrderCardRouteFillPercent({ hasDriver, pickedUp, progress, rawOrder }) {
  if (rawOrder && isOrderDelivered(rawOrder)) return 100;
  if (!hasDriver) return 0;
  if (pickedUp) {
    const p = Number(progress);
    if (Number.isFinite(p) && p > 0) {
      return Math.min(100, Math.max(50, p));
    }
    return 68;
  }
  return 50;
}
